import React from "react";
import type { paths } from "../../api/types";
import { Button } from "../../components/common";
import { type PluginTranslationKey } from "./translations";

export type Connection =
  paths["/api/v1/plugin/connections"]["get"]["responses"][200]["content"]["application/json"]["items"][number];

const scopeKeyMap: Record<string, PluginTranslationKey> = {
  "priceloss:read": "scope.read",
  "priceloss:write": "scope.write",
  "priceloss:events": "scope.events",
};

function formatDate(dateStr: string, lang: string): string {
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString(lang === "ar" ? "ar-SA" : lang, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export interface ConnectionTableProps {
  connections: Connection[];
  loading: boolean;
  revokingId: string | null;
  onRevoke: (id: string) => void;
  language: string;
  t: (
    key: PluginTranslationKey,
    params?: Record<string, string | number>,
  ) => string;
  offset: number;
  limit: number;
  onPrevPage: () => void;
  onNextPage: () => void;
}

export const ConnectionTable: React.FC<ConnectionTableProps> = ({
  connections,
  loading,
  revokingId,
  onRevoke,
  language,
  t,
  offset,
  limit,
  onPrevPage,
  onNextPage,
}) => {
  if (loading && connections.length === 0) {
    return (
      <div role="status" className="py-8 text-center text-sm text-slate-500">
        {t("state.loading")}
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <div className="py-8 text-center space-y-1">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t("state.empty")}
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("state.empty_hint")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-start text-xs border-collapse">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400">
              <th className="py-2.5 px-3 font-semibold text-start">
                {t("table.client")}
              </th>
              <th className="py-2.5 px-3 font-semibold text-start">
                {t("table.permissions")}
              </th>
              <th className="py-2.5 px-3 font-semibold text-start">
                {t("table.connected")}
              </th>
              <th className="py-2.5 px-3 font-semibold text-start">
                {t("table.status")}
              </th>
              <th className="py-2.5 px-3 font-semibold text-end">
                {t("table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {connections.map((conn) => (
              <tr
                key={conn.id}
                className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30 transition-colors"
              >
                <td className="py-3 px-3 font-medium text-slate-900 dark:text-slate-100">
                  <bdi>{conn.client_id}</bdi>
                </td>
                <td className="py-3 px-3">
                  <div className="flex flex-wrap gap-1">
                    {conn.scopes.map((scope) => (
                      <span
                        key={scope}
                        className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px]"
                      >
                        {scopeKeyMap[scope] ? t(scopeKeyMap[scope]) : scope}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-3 px-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  {formatDate(conn.created_at, language)}
                </td>
                <td className="py-3 px-3">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      conn.revoked_at
                        ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800"
                    }`}
                  >
                    {conn.revoked_at ? t("status.revoked") : t("status.active")}
                  </span>
                </td>
                <td className="py-3 px-3 text-end">
                  <Button
                    size="sm"
                    variant="outline"
                    className="min-h-11 px-3 text-xs"
                    disabled={
                      Boolean(conn.revoked_at) || revokingId === conn.id
                    }
                    isLoading={revokingId === conn.id}
                    onClick={() => onRevoke(conn.id)}
                    aria-label={`${t("action.revoke")} ${conn.client_id}`}
                  >
                    {revokingId === conn.id
                      ? t("action.revoking")
                      : t("action.revoke")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(offset > 0 || connections.length >= limit) && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-800">
          <Button
            size="sm"
            variant="outline"
            className="min-h-11 px-4 text-xs"
            disabled={offset === 0 || loading}
            onClick={onPrevPage}
          >
            {t("pagination.prev")}
          </Button>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t("pagination.page", { page: Math.floor(offset / limit) + 1 })}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="min-h-11 px-4 text-xs"
            disabled={connections.length < limit || loading}
            onClick={onNextPage}
          >
            {t("pagination.next")}
          </Button>
        </div>
      )}
    </div>
  );
};
